import { useLayoutEffect, useRef, useState } from 'react';
import styles from './Watermark.module.css';

/**
 * Watermark overlay (piece 3): the given text repeats diagonally at -30 degrees
 * and 8% opacity in the inactive grey, sized by clamp(32px, 8vw, 64px) and
 * tiled across the document's full scrollable height with no gap. The overlay
 * is absolutely positioned inside the document's container, layered above the
 * content and below chrome, and intercepts no click or selection.
 */
export interface WatermarkProps {
  text: string;
}

export const watermarkDocumentedStates = ['viewOnly'] as const;

export function Watermark({ text }: WatermarkProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(24);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const size = Math.max(32, Math.min(64, el.clientWidth * 0.08));
      const columns = Math.max(1, Math.ceil((el.clientWidth * 2) / (size * 5)));
      const rows = Math.max(1, Math.ceil((el.clientHeight * 2) / (size * 2)));
      setCount(Math.min(2000, Math.max(24, columns * rows)));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [text]);

  return (
    <div ref={ref} className={styles.overlay} aria-hidden="true" data-watermark={text}>
      <div className={styles.tiles}>
        {Array.from({ length: count }, (_, i) => (
          <span key={i} className={styles.tile} data-text={text} />
        ))}
      </div>
    </div>
  );
}
